<script setup>
/**
 * WHAT HAPPENS, IN ORDER — the ordered list an owner writes, and the only screen on which
 * the shape of the whole task is visible at once.
 *
 * ── WHAT THE LIST MEANS, SAID ON THE SCREEN ITSELF ────────────────────────────────────
 * "Waslo works down this list." A row's onward link IS the row under it, so there is no
 * `then →` control on a row and no card position that means nothing. Anything that is not
 * "carry on" is an outcome the owner chose on purpose, and only THOSE are drawn — a jump
 * says where it goes, a stop says it stops.
 *
 * AND NEVER ON A HAND-OVER OR AN ENDING. A `next` on a `handoff` or an `end` step is dead
 * in the engine and round-trips as a real "then", so a list that drew it would be showing
 * an owner a link that does nothing — a lie with a graph behind it. `task-spine.js`'s
 * `FINAL_TYPES` is the same set, read from the same place.
 *
 * ── `+ ADD A STEP` BETWEEN EVERY ROW ──────────────────────────────────────────────────
 * The affordance goes where the attention already is. A single "Add a step" at the bottom
 * makes inserting into the middle a two-move operation — add, then move it up four times —
 * on the one list where moving is a real edit to what the assistant does.
 *
 * ── AND THE THING THAT IS NOT A SORT ──────────────────────────────────────────────────
 * Position writes `next`, so ▲▼ changes what a live conversation does. Three things say so:
 * the button's own accessible name states the consequence by name ("Waslo will ask Which
 * day before Which studio"); a move that would make a connection read an answer nothing has
 * saved yet is REFUSED in place, naming the input, from `moveProblem` — the same
 * `input_before_save` rule the server enforces; and nothing here is written to the server
 * until the save bar is pressed, so the refusal is the last gate rather than the first
 * apology.
 */
import { computed, ref } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';

import StepKindPicker from './StepKindPicker.vue';
import { useWasloI18n } from '../../i18n.js';
import { toIntlLocale } from '../../helpers/intl.js';
import { ANSWER_ROWS, rowForStep } from '../../helpers/task-answer-kinds.js';
import { problemMessage } from '../../helpers/task-flow-draft.js';

const props = defineProps({
  /** The spine's rows, in order. */
  rows: { type: Array, default: () => [] },
  /** `{ [stepId]: Problem[] }` — only what belongs to this screen. */
  problems: { type: Object, default: () => ({}) },
  /** `stepId => routeLocation`, so a row is a real link. */
  linkFor: { type: Function, required: true },
  /** `(index, delta) => {stepId, detail}|null` — the move this list must refuse. */
  moveProblem: { type: Function, default: () => null },
});

const emit = defineEmits(['add', 'move']);

const { t, locale } = useWasloI18n();

const key = name => `WASLO.TASKS.COMPOSE.LIST.${name}`;

const format = computed(
  () => new Intl.NumberFormat(toIntlLocale(locale.value))
);

/** Which row of `+ add a step` is open, as an insertion index. `null` is none. */
const adding = ref(null);
/** The refusal currently on screen, as `{ index, delta, detail }`. */
const refused = ref(null);

const ANSWER_LABEL = new Map(ANSWER_ROWS.map(row => [row.id, row.labelKey]));

const FINAL_TYPES = new Set(['handoff', 'end']);

const titleOf = step => {
  if (step.type === 'ask' || step.type === 'choose') {
    return (step.questionEn ?? '').trim() || (step.name ?? '').trim();
  }
  return (step.name ?? '').trim() || (step.instruction ?? '').trim();
};

const kindLabel = step => {
  const answerRow = rowForStep(step);
  if (answerRow) return t(ANSWER_LABEL.get(answerRow));
  return t(key(`KIND_${String(step.type ?? '').toUpperCase()}`));
};

const nameOf = id => {
  const found = props.rows.find(row => row?.step?.id === id);
  return found ? titleOf(found.step) || id : id;
};

/**
 * The one sentence under a row that is not "carry on down the list", or `''`.
 *
 * Nothing on a hand-over or an ending, whatever the stored graph says — see the header.
 */
const outcomeOf = row => {
  const type = row?.step?.type;
  if (FINAL_TYPES.has(type)) return '';
  const then = row?.then;
  if (then?.go === 'step')
    return t(key('THEN_GO'), { name: nameOf(then.step) });
  if (then?.go === 'stop') return t(key('THEN_STOP'));
  if (then?.go === 'handOver') return t(key('THEN_HANDOVER'));
  return '';
};

const list = computed(() =>
  props.rows.map((row, index) => ({
    index,
    id: row?.step?.id ?? '',
    number: format.value.format(index + 1),
    title: titleOf(row?.step ?? {}) || t(key('UNWRITTEN')),
    written: titleOf(row?.step ?? {}) !== '',
    kind: kindLabel(row?.step ?? {}),
    outcome: outcomeOf(row),
    problems: (props.problems[row?.step?.id] ?? []).map(problem =>
      problemMessage(problem, t)
    ),
    isFirst: index === 0,
    isLast: index === props.rows.length - 1,
  }))
);

/** "Waslo will ask Which day before Which studio." Stated on the button, not after it. */
const moveLabel = (index, delta) => {
  const here = props.rows[index];
  const there = props.rows[index + delta];
  if (!here || !there) return t(key(delta < 0 ? 'UP' : 'DOWN'));
  const moved = titleOf(here.step) || here.step?.id;
  const other = titleOf(there.step) || there.step?.id;
  return t(key(delta < 0 ? 'UP_MEANS' : 'DOWN_MEANS'), {
    moved,
    other,
  });
};

const move = (index, delta) => {
  const problem = props.moveProblem(index, delta);
  if (problem) {
    refused.value = { index, delta, detail: problem.detail };
    return;
  }
  refused.value = null;
  emit('move', { index, delta });
};

const openAdd = index => {
  adding.value = adding.value === index ? null : index;
};

const pick = (index, kind) => {
  adding.value = null;
  emit('add', { index, kind });
};
</script>

<template>
  <section class="flex flex-col gap-4" data-waslo-spine-list>
    <div class="flex flex-col gap-1">
      <h2 class="mb-0 text-base font-medium text-n-slate-12">
        {{ t(key('HEADING')) }}
      </h2>
      <p class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11">
        {{ t(key('DESCRIPTION')) }}
      </p>
    </div>

    <p
      v-if="rows.length === 0"
      class="mb-0 text-sm text-n-slate-11"
      data-waslo-spine-empty
    >
      {{ t(key('EMPTY')) }}
    </p>

    <ol class="flex flex-col gap-0 mb-0">
      <li v-for="row in list" :key="row.id" class="flex flex-col">
        <!-- Insert above this row. -->
        <div class="flex justify-center py-1">
          <Button
            v-if="adding !== row.index"
            :label="t(key('ADD'))"
            icon="i-lucide-plus"
            variant="link"
            color="slate"
            size="sm"
            :data-waslo-spine-add="row.index"
            @click="openAdd(row.index)"
          />
        </div>
        <StepKindPicker
          v-if="adding === row.index"
          class="mb-2"
          @pick="kind => pick(row.index, kind)"
          @cancel="adding = null"
        />

        <div
          class="flex items-start gap-3 p-4 outline outline-1 rounded-xl bg-n-solid-1"
          :class="row.problems.length ? 'outline-n-ruby-8' : 'outline-n-weak'"
          :data-waslo-task-step="row.id"
          :data-waslo-spine-row="row.id"
        >
          <router-link
            :to="linkFor(row.id)"
            class="flex items-start flex-1 min-w-0 gap-3 text-start"
            :data-waslo-spine-open="row.id"
          >
            <span
              class="grid text-xs font-medium rounded-full shrink-0 place-items-center size-6 bg-n-alpha-2 text-n-slate-11"
              aria-hidden="true"
            >
              {{ row.number }}
            </span>
            <span class="flex flex-col min-w-0 gap-1">
              <span
                class="text-sm font-medium break-words"
                :class="row.written ? 'text-n-slate-12' : 'text-n-slate-10'"
              >
                <bdi>{{ row.title }}</bdi>
              </span>
              <span class="text-sm text-n-slate-11">{{ row.kind }}</span>
              <span
                v-if="row.outcome"
                class="text-sm text-n-slate-11"
                :data-waslo-spine-outcome="row.id"
              >
                {{ row.outcome }}
              </span>
              <span
                v-for="(line, at) in row.problems"
                :key="at"
                class="flex items-start gap-2 text-sm text-n-ruby-9"
                data-waslo-spine-problem
              >
                <span
                  class="mt-0.5 shrink-0 size-4 i-lucide-triangle-alert"
                  aria-hidden="true"
                />
                <span>{{ line }}</span>
              </span>
            </span>
          </router-link>

          <div class="flex items-center gap-1 shrink-0">
            <button
              type="button"
              class="grid rounded-md place-items-center size-7 text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-40 disabled:hover:bg-transparent"
              :disabled="row.isFirst"
              :aria-label="moveLabel(row.index, -1)"
              :title="moveLabel(row.index, -1)"
              :data-waslo-spine-up="row.id"
              @click="move(row.index, -1)"
            >
              <span class="size-4 i-lucide-arrow-up" aria-hidden="true" />
            </button>
            <button
              type="button"
              class="grid rounded-md place-items-center size-7 text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-40 disabled:hover:bg-transparent"
              :disabled="row.isLast"
              :aria-label="moveLabel(row.index, 1)"
              :title="moveLabel(row.index, 1)"
              :data-waslo-spine-down="row.id"
              @click="move(row.index, 1)"
            >
              <span class="size-4 i-lucide-arrow-down" aria-hidden="true" />
            </button>
          </div>
        </div>

        <!--
          The refusal, in place, under the row it is about — never a toast, because the
          thing it names is two rows away and a toast takes both off screen.
        -->
        <p
          v-if="refused && refused.index === row.index"
          class="mt-2 mb-0 text-sm text-n-amber-11"
          data-waslo-spine-move-refused
        >
          {{ t(key('MOVE_REFUSED'), { detail: refused.detail }) }}
        </p>
      </li>
    </ol>

    <!-- Insert at the end. -->
    <div class="flex justify-center">
      <Button
        v-if="adding !== rows.length"
        :label="t(key('ADD'))"
        icon="i-lucide-plus"
        variant="faded"
        color="slate"
        size="sm"
        :data-waslo-spine-add="rows.length"
        @click="openAdd(rows.length)"
      />
    </div>
    <StepKindPicker
      v-if="adding === rows.length"
      @pick="kind => pick(rows.length, kind)"
      @cancel="adding = null"
    />
  </section>
</template>
